import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Card, CardContent, Grid, Avatar, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Stack,
  ToggleButton, ToggleButtonGroup, List, ListItem, ListItemIcon, ListItemText, Divider, Alert, Link, Snackbar,
  MenuItem, InputAdornment, Switch, FormControlLabel, Checkbox, Tooltip,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import adminApplicationsService from '../services/adminApplicationsService';
import DocumentCompletenessStrip from '../components/admin/DocumentCompletenessStrip';
import { ZIM_PROVINCES } from '../constants/provinces';
import { useFeatures } from '../contexts/FeaturesContext';

// 'archived' = still pending, but set aside after four unanswered document
// reminders; any upload puts them back in 'pending'.
const STATUSES = ['pending', 'approved', 'rejected', 'archived'];
const ROLE_FILTERS = [
  { value: 'all', label: 'All roles' },
  { value: 'contractor', label: 'Contractors' },
  { value: 'verifier', label: 'Verifiers' },
  // Realtors only exist while the property-listings vertical is on.
  { value: 'realtor', label: 'Realtors', feature: 'listings' },
  { value: 'conveyancer', label: 'Conveyancers', feature: 'listings' },
];

const DOC_TYPE_LABELS = {
  id_card: 'Identity document (ID card)',
  passport: 'Passport',
  license: 'License',
  insurance: 'Insurance certificate',
  certificate: 'Certificate',
  portfolio: 'Portfolio item',
  company_registration: 'Company registration',
  director_id: 'Owner/director ID',
  tax_clearance: 'Tax clearance',
  eac_practising_certificate: 'EAC practising certificate',
  agency_engagement_letter: 'Agency letter of engagement',
  lsz_practising_certificate: 'Practising certificate (Law Society)',
  other: 'Other document',
};

const DOC_STATUS_COLOR = {
  approved: 'success',
  rejected: 'error',
  flagged: 'warning',
  pending: 'default',
};

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const timeAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days}d ago`;
  const hrs = Math.floor(ms / 3600000);
  if (hrs >= 1) return `${hrs}h ago`;
  return 'just now';
};

const formatTier = (t) => {
  if (!t) return '—';
  const s = String(t).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function AdminApplicationsPage() {
  const [view, setView] = useState('applications'); // 'applications' | 'tierUpgrades'
  const [status, setStatus] = useState('pending');
  const [role, setRole] = useState('all');
  const [province, setProvince] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState(''); // debounced value used for fetching
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null); // app being rejected
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reviewing, setReviewing] = useState(null); // app being reviewed (dialog)
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const [bulkReminding, setBulkReminding] = useState(false);
  const [toast, setToast] = useState('');
  const [kycRequired, setKycRequired] = useState(true);
  const [kycBusy, setKycBusy] = useState(false);
  const [waiveBusy, setWaiveBusy] = useState(false);
  const [verifierLevels, setVerifierLevels] = useState([]); // chosen client/contractor levels
  const [verifierBusy, setVerifierBusy] = useState(false);
  const [tierUpgrades, setTierUpgrades] = useState([]);
  const [tierLoading, setTierLoading] = useState(false);
  const [tierBusyId, setTierBusyId] = useState(null);
  const [merchantDocs, setMerchantDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docBusyId, setDocBusyId] = useState(null);
  const [docRejecting, setDocRejecting] = useState(null);
  const [docNotes, setDocNotes] = useState('');
  const [reqWaiveBusyKey, setReqWaiveBusyKey] = useState(null); // key of requirement being waived
  const features = useFeatures();
  const roleFilters = ROLE_FILTERS.filter((r) => !r.feature || features[r.feature]);
  const [eacBusy, setEacBusy] = useState(false);
  const [eacOverriding, setEacOverriding] = useState(null);
  const [eacError, setEacError] = useState('');
  const [eacNotes, setEacNotes] = useState('');

  const isVerifierRole = (r) => r === 'verifier' || r === 'professional_verifier';
  // Sync the level checkboxes whenever a new application detail loads.
  useEffect(() => {
    const vt = detail?.verifierTypes || reviewing?.verifierTypes;
    if (reviewing && isVerifierRole(reviewing.role)) {
      setVerifierLevels(Array.isArray(vt) && vt.length ? vt : ['client']);
    }
  }, [detail, reviewing]);

  const toggleLevel = (lvl) => setVerifierLevels((prev) =>
    prev.includes(lvl) ? prev.filter((l) => l !== lvl) : [...prev, lvl]);

  useEffect(() => {
    adminApplicationsService.getKycSettings()
      .then((r) => setKycRequired(r.data?.contractorKycRequired !== false))
      .catch(() => {});
  }, []);

  const toggleKyc = async (next) => {
    setKycBusy(true);
    try {
      await adminApplicationsService.setKycSettings(next);
      setKycRequired(next);
      setToast(next ? 'Contractor documents are now required' : 'Contractor documents waived for ALL contractors');
      load();
    } catch (e) { setToast('Failed to update setting'); } finally { setKycBusy(false); }
  };

  const waiveApplicant = async (app, waived) => {
    setWaiveBusy(true);
    try {
      await adminApplicationsService.waiveDocuments(app.id, waived, waived ? 'Waived by admin' : null);
      setToast(waived ? `Documents waived for ${app.email}` : `Waiver removed for ${app.email}`);
      if (reviewing && reviewing.id === app.id) {
        const res = await adminApplicationsService.detail(app.id);
        setDetail(res.data || res);
      }
      load();
    } catch (e) { setToast('Failed to update waiver'); } finally { setWaiveBusy(false); }
  };

  const waiveRequirement = async (key, waived) => {
    if (!reviewing) return;
    setReqWaiveBusyKey(key);
    // Optionally ask for a reason when waiving (not when un-waiving).
    let reason = null;
    if (waived) {
      // eslint-disable-next-line no-alert
      reason = window.prompt('Reason for waiving (optional)') || null;
    }
    try {
      await adminApplicationsService.waiveRequirement(reviewing.id, key, waived, reason);
      setToast(waived ? 'Requirement waived' : 'Waiver removed');
      await refreshDetail();
    } catch (e) {
      setToast(e?.response?.data?.error || 'Failed to update waiver');
    } finally {
      setReqWaiveBusyKey(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApplicationsService.list({
        status,
        limit: 100,
        ...(role !== 'all' && { role }),
        ...(province !== 'all' && { province }),
        ...(search && { search }),
      });
      setApps(data.applications || []);
    } finally { setLoading(false); }
  }, [status, role, province, search]);

  useEffect(() => { load(); }, [load]);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openReview = async (app) => {
    setReviewing(app);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await adminApplicationsService.detail(app.id);
      setDetail(res?.data || res || null);
    } catch (e) {
      setDetail({ error: true });
    } finally {
      setDetailLoading(false);
    }
  };
  const closeReview = () => { setReviewing(null); setDetail(null); };

  // Re-fetch the open application's documents after a per-doc decision so the
  // status chips update without closing the dialog.
  const refreshDetail = async () => {
    if (!reviewing) return;
    try {
      const res = await adminApplicationsService.detail(reviewing.id);
      setDetail(res?.data || res || null);
    } catch (e) { /* keep prior detail on a transient error */ }
  };
  const setDocStatus = async (docId, decision) => {
    setBusyId(docId);
    try {
      if (decision === 'approved') await adminApplicationsService.approveDocument(docId);
      else await adminApplicationsService.rejectDocument(docId);
      await refreshDetail();
    } finally { setBusyId(null); }
  };

  const approve = async (id) => {
    setBusyId(id);
    // For verifiers, send the chosen levels (client/contractor) with the approval.
    const verifierTypes = reviewing && reviewing.id === id && isVerifierRole(reviewing.role)
      ? verifierLevels : undefined;
    try {
      await adminApplicationsService.approve(id, verifierTypes);
      closeReview();
      await load();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to approve application';
      setToast(msg);
    } finally { setBusyId(null); }
  };

  // Update an already-approved verifier's levels without re-approving.
  const saveVerifierLevels = async (id) => {
    setVerifierBusy(true);
    try {
      await adminApplicationsService.setVerifierTypes(id, verifierLevels);
      setToast('Verifier levels updated');
      await refreshDetail();
      await load();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed to update levels'); }
    finally { setVerifierBusy(false); }
  };
  // Undo a mistaken approve/reject: moves the applicant back to 'pending'
  // (re-gates their access). Silent — no email is sent to the applicant.
  const unapprove = async (id) => {
    setBusyId(id);
    try {
      await adminApplicationsService.unapprove(id);
      closeReview();
      setToast('Reverted to pending — moved back to the review queue (no email sent).');
      await load();
    } finally { setBusyId(null); }
  };
  const submitReject = async () => {
    if (!rejecting || reason.trim().length < 3) return;
    setBusyId(rejecting.id);
    try {
      await adminApplicationsService.reject(rejecting.id, reason.trim());
      setRejecting(null); setReason(''); closeReview(); await load();
    } finally { setBusyId(null); }
  };

  const remind = async (id) => {
    setRemindingId(id);
    try {
      const res = await adminApplicationsService.remind(id);
      const reason = res?.data?.reason;
      setToast(
        res?.data?.sent
          ? 'Reminder sent.'
          : reason === 'pending_review'
            ? 'Nothing to remind — all documents are uploaded and pending review.'
            : 'Nothing to remind — documents are complete.'
      );
      await load();
    } catch (e) {
      setToast('Failed to send reminder.');
    } finally { setRemindingId(null); }
  };

  const remindAll = async () => {
    setBulkReminding(true);
    try {
      const res = await adminApplicationsService.remindIncomplete(role);
      const d = res?.data || {};
      setToast(`Reminded ${d.emailed || 0} applicant(s); ${d.skippedCooldown || 0} on cooldown, ${d.skippedComplete || 0} complete.`);
      await load();
    } catch (e) {
      setToast('Failed to send reminders.');
    } finally { setBulkReminding(false); }
  };

  const loadTierUpgrades = useCallback(async () => {
    setTierLoading(true);
    try {
      const res = await adminApplicationsService.listTierUpgrades();
      setTierUpgrades(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setToast('Failed to load tier upgrades.');
    } finally { setTierLoading(false); }
  }, []);

  useEffect(() => {
    if (view === 'tierUpgrades') loadTierUpgrades();
  }, [view, loadTierUpgrades]);

  const resolveTierUpgrade = async (row, approve) => {
    setTierBusyId(row.id);
    try {
      await adminApplicationsService.resolveTierUpgrade(row.id, approve);
      setToast(approve
        ? `Approved ${row.business_name || 'merchant'} → ${formatTier(row.requested_tier)}`
        : `Rejected tier upgrade for ${row.business_name || 'merchant'}`);
      await loadTierUpgrades();
    } catch (e) {
      setToast(e?.response?.data?.error || 'Failed to resolve tier upgrade.');
    } finally { setTierBusyId(null); }
  };

  const loadMerchantDocs = useCallback(async () => {
    setDocsLoading(true);
    try {
      const res = await adminApplicationsService.listMerchantDocuments('Pending');
      setMerchantDocs(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setToast('Failed to load merchant documents.');
    } finally { setDocsLoading(false); }
  }, []);

  useEffect(() => {
    if (view === 'merchantDocs') loadMerchantDocs();
  }, [view, loadMerchantDocs]);

  const reviewMerchantDoc = async (row, decision, notes) => {
    setDocBusyId(row.id);
    try {
      await adminApplicationsService.reviewMerchantDocument(row.id, decision, notes);
      setToast(`${decision} ${row.document_type?.replace(/_/g, ' ')} for ${row.business_name || 'merchant'}`);
      setDocRejecting(null);
      setDocNotes('');
      await loadMerchantDocs();
    } catch (e) {
      setToast(e?.response?.data?.error || 'Failed to review document.');
    } finally { setDocBusyId(null); }
  };

  const setEacOverride = async (row, verified, notes) => {
    setEacBusy(true);
    setEacError('');
    try {
      if (row.role === 'conveyancer') {
        await adminApplicationsService.setConveyancerLszOverride(row.id, verified, notes);
        setToast(verified ? 'Conveyancer marked as manually verified' : 'Manual verification removed');
      } else {
        await adminApplicationsService.setRealtorEacOverride(row.id, verified, notes);
        setToast(verified ? 'Agency marked as manually verified' : 'Manual verification removed');
      }
      setEacOverriding(null); setEacNotes('');
      await openReview(row);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to update EAC verification.';
      // The notes dialog sits above the review dialog, so a snackbar is hidden — show it inline too.
      setEacError(msg);
      setToast(msg);
    } finally { setEacBusy(false); }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Applications</Typography>
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Switch checked={kycRequired} disabled={kycBusy} onChange={(e) => toggleKyc(e.target.checked)} />}
          label={<Typography variant="body2">{kycRequired ? 'Contractor documents required' : 'Contractor documents waived (all)'}</Typography>}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and verify contractor &amp; verifier applications.
      </Typography>

      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 3 }}>
        <Tab value="applications" label="Applications" />
        <Tab value="tierUpgrades" label="Merchant tier upgrades" />
        <Tab value="merchantDocs" label="Merchant documents" />
      </Tabs>

      {view === 'applications' && (
      <>
      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2 }}>
        {STATUSES.map((s) => <Tab key={s} value={s} label={s[0].toUpperCase() + s.slice(1)} />)}
      </Tabs>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={role}
        onChange={(_, v) => { if (v !== null) setRole(v); }}
        sx={{ mb: 3 }}
        aria-label="Filter applications by role"
      >
        {roleFilters.map((r) => (
          <ToggleButton key={r.value} value={r.value} sx={{ textTransform: 'none', px: 2 }}>
            {r.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, email or phone"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 260, maxWidth: 440 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <Button size="small" sx={{ minWidth: 'auto' }} onClick={() => setSearchInput('')}>Clear</Button>
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          select size="small" label="Province" value={province}
          onChange={(e) => setProvince(e.target.value)} sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All provinces</MenuItem>
          {ZIM_PROVINCES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        {!loading && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {apps.length} {apps.length === 1 ? 'result' : 'results'}
          </Typography>
        )}
      </Stack>

      {status === 'pending' && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" disabled={bulkReminding} onClick={remindAll}>
            {bulkReminding ? 'Sending…' : 'Remind all incomplete'}
          </Button>
        </Box>
      )}

      {loading ? <CircularProgress /> : apps.length === 0 ? (
        <Typography color="text.secondary">
          No {status}{role !== 'all' ? ` ${role}` : ''} applications.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {apps.map((a) => (
            <Grid item xs={12} md={6} key={a.id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar src={a.profileImageUrl || undefined}>{(a.firstName || a.email)[0]}</Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{a.firstName} {a.lastName}</Typography>
                      <Typography variant="body2" color="text.secondary">{a.email}</Typography>
                    </Box>
                    <Chip size="small" label={a.role} />
                  </Stack>
                  <Typography variant="body2">Province: {a.province || a.city || 'Unspecified'}</Typography>
                  <Typography variant="body2">Experience: {a.yearsExperience ?? '—'} yrs</Typography>
                  <DocumentCompletenessStrip summary={a.documentSummary} />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="outlined" startIcon={<DescriptionOutlinedIcon />}
                      onClick={() => openReview(a)}>Review documents</Button>
                    {status === 'pending' && (
                      <>
                        <Button size="small" variant="contained" disabled={busyId === a.id}
                          onClick={() => approve(a.id)}>Approve</Button>
                        <Button size="small" color="error" variant="outlined" disabled={busyId === a.id}
                          onClick={() => { setRejecting(a); setReason(''); }}>Reject</Button>
                        <Button size="small" variant="text" disabled={remindingId === a.id || a.documentSummary?.allSubmitted}
                          onClick={() => remind(a.id)}>
                          {a.lastRemindedAt ? `Remind again (${timeAgo(a.lastRemindedAt)})` : 'Send reminder'}
                        </Button>
                      </>
                    )}
                    {status !== 'pending' && (
                      <Button size="small" color="warning" variant="outlined" disabled={busyId === a.id}
                        onClick={() => unapprove(a.id)}>
                        {status === 'approved' ? 'Un-approve' : 'Move to pending'}
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Document review */}
      <Dialog open={!!reviewing} onClose={closeReview} fullWidth maxWidth="sm">
        <DialogTitle>
          {reviewing ? `${reviewing.firstName || ''} ${reviewing.lastName || ''}`.trim() || reviewing.email : ''}
          {reviewing && <Chip size="small" label={reviewing.role} sx={{ ml: 1 }} />}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : detail?.error ? (
            <Alert severity="error">Couldn't load this application's documents.</Alert>
          ) : (
            <>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">{reviewing?.email}</Typography>
                <Typography variant="body2">
                  Province: {detail?.profile?.province || reviewing?.province || '—'}
                  {detail?.profile?.district ? ` · ${detail.profile.district}` : ''}
                </Typography>
                {detail?.profile?.company_name && (
                  <Typography variant="body2">Company: {detail.profile.company_name}</Typography>
                )}
                {detail?.profile?.registration_number && (
                  <Typography variant="body2">Reg. number: {detail.profile.registration_number}</Typography>
                )}
              </Stack>

              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  Submitted documents ({detail?.documents?.length || 0})
                </Typography>
                {reviewing?.role === 'contractor' && (
                  detail?.documentsWaived ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" color="info" label="Documents waived" />
                      <Button size="small" disabled={waiveBusy} onClick={() => waiveApplicant(reviewing, false)}>Remove waiver</Button>
                    </Stack>
                  ) : (
                    <Button size="small" variant="outlined" disabled={waiveBusy} onClick={() => waiveApplicant(reviewing, true)}>
                      Waive documents
                    </Button>
                  )
                )}
              </Stack>

              {reviewing?.role === 'realtor' && detail?.eacStatus && (
                <Alert
                  severity={detail.eacStatus.state === 'approved' ? 'success' : detail.eacStatus.state === 'rejected' ? 'error' : 'warning'}
                  sx={{ mb: 1 }}
                  action={
                    detail.eacStatus.manualOverride ? (
                      <Button size="small" disabled={eacBusy} onClick={() => setEacOverride(reviewing, false)}>Remove manual verification</Button>
                    ) : detail.eacStatus.state !== 'approved' ? (
                      <Button size="small" disabled={eacBusy} onClick={() => { setEacOverriding(reviewing); setEacNotes(''); }}>Verify agency manually</Button>
                    ) : null
                  }
                >
                  <strong>Agency on EAC register:</strong>{' '}
                  {detail.eacStatus.manualOverride
                    ? `verified manually — ${detail.eacStatus.manualOverride.notes}`
                    : detail.eacStatus.agency
                      ? `${detail.eacStatus.agency.name} · ${detail.eacStatus.agency.status}${detail.eacStatus.agency.disappeared_at ? ' · no longer listed' : ''}`
                      : detail.eacStatus.registerAvailable
                        ? `no match${detail.eacStatus.agencyNameClaimed ? ` for "${detail.eacStatus.agencyNameClaimed}"` : ''}`
                        : 'register unavailable — verify manually'}
                  {detail.eacStatus.registerLastSyncedAt && ` (register synced ${formatDate(detail.eacStatus.registerLastSyncedAt)})`}
                </Alert>
              )}

              {reviewing?.role === 'conveyancer' && detail?.lszStatus && (
                <Alert
                  severity={detail.lszStatus.state === 'approved' ? 'success' : detail.lszStatus.state === 'rejected' ? 'error' : 'warning'}
                  sx={{ mb: 1 }}
                  action={
                    detail.lszStatus.manualOverride ? (
                      <Button size="small" disabled={eacBusy} onClick={() => setEacOverride(reviewing, false)}>Remove manual verification</Button>
                    ) : detail.lszStatus.state !== 'approved' ? (
                      <Button size="small" disabled={eacBusy} onClick={() => { setEacOverriding(reviewing); setEacNotes(''); }}>Verify manually</Button>
                    ) : null
                  }
                >
                  <strong>Law Society register:</strong>{' '}
                  {detail.lszStatus.manualOverride
                    ? `verified manually — ${detail.lszStatus.manualOverride.notes}`
                    : detail.lszStatus.practitioner
                      ? `${detail.lszStatus.practitioner.surname}${detail.lszStatus.practitioner.first_names ? `, ${detail.lszStatus.practitioner.first_names}` : ''}${detail.lszStatus.practitioner.firm ? ` · ${detail.lszStatus.practitioner.firm}` : ''}${detail.lszStatus.practitioner.disappeared_at ? ' · no longer in the directory' : detail.lszStatus.practitioner.is_conveyancer ? ' · conveyancer' : ' · not a conveyancer'}`
                      : detail.lszStatus.registerAvailable
                        ? 'not linked to a directory entry yet'
                        : 'register unavailable — verify manually'}
                  {detail.lszStatus.registerLastSyncedAt && ` (register synced ${formatDate(detail.lszStatus.registerLastSyncedAt)})`}
                </Alert>
              )}

              {!detail?.documentsWaived && detail?.documentChecklist?.items?.some((i) => i.status === 'missing' || i.status === 'rejected') && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Missing / needs re-upload:{' '}
                  {detail.documentChecklist.items
                    .filter((i) => i.status === 'missing' || i.status === 'rejected')
                    .map((i) => i.label)
                    .join(', ')}
                </Alert>
              )}

              {(!detail?.documents || detail.documents.length === 0) ? (
                <Alert severity="warning">
                  This applicant hasn't uploaded any documents yet. There's nothing to verify —
                  consider rejecting with a note asking them to complete their profile, or hold off
                  on approving.
                </Alert>
              ) : (
                <List dense>
                  {detail.documents.map((d) => (
                    <ListItem key={d.id} disableGutters alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <span>{DOC_TYPE_LABELS[d.documentType] || d.documentType}</span>
                            <Chip size="small"
                              label={d.verificationStatus || 'pending'}
                              color={DOC_STATUS_COLOR[d.verificationStatus] || 'default'}
                              variant={d.verificationStatus === 'approved' ? 'filled' : 'outlined'} />
                          </Stack>
                        }
                        secondary={[d.documentName, formatBytes(d.fileSize)].filter(Boolean).join(' · ')}
                      />
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 5, mt: 0.5 }}>
                        {d.fileUrl && (
                          <Link href={d.fileUrl} target="_blank" rel="noopener" underline="none">
                            <Button size="small" startIcon={<OpenInNewIcon />}>View</Button>
                          </Link>
                        )}
                        {d.verificationStatus !== 'approved' && (
                          <Button size="small" color="success" variant="outlined" disabled={busyId === d.id}
                            onClick={() => setDocStatus(d.id, 'approved')}>Approve</Button>
                        )}
                        {d.verificationStatus !== 'rejected' && (
                          <Button size="small" color="error" disabled={busyId === d.id}
                            onClick={() => setDocStatus(d.id, 'rejected')}>Reject</Button>
                        )}
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}

          {detail && detail.applicationChecklist && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>Mandatory requirements</Typography>
              {!detail.applicationChecklist.complete && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Some requirements are unmet. Waive individual items or resolve them before approving.
                </Alert>
              )}
              <List dense disablePadding>
                {detail.applicationChecklist.items.map((item) => {
                  const isBusy = reqWaiveBusyKey === item.key;
                  if (item.satisfied && !item.waived) {
                    // Met requirement
                    return (
                      <ListItem key={item.key} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutlineIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </ListItem>
                    );
                  }
                  if (item.waived) {
                    // Waived requirement
                    return (
                      <ListItem key={item.key} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutlineIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" color="info" label="Waived" />
                          <Button size="small" disabled={isBusy}
                            onClick={() => waiveRequirement(item.key, false)}>
                            {isBusy ? '…' : 'Un-waive'}
                          </Button>
                        </Stack>
                      </ListItem>
                    );
                  }
                  if (item.optional) {
                    // Optional + not satisfied — no action needed
                    return (
                      <ListItem key={item.key} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <WarningAmberIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary="Optional"
                          secondaryTypographyProps={{ color: 'text.disabled' }}
                        />
                      </ListItem>
                    );
                  }
                  // Mandatory + not satisfied
                  return (
                    <ListItem key={item.key} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <WarningAmberIcon fontSize="small" color="warning" />
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                      <Button size="small" variant="outlined" color="warning" disabled={isBusy}
                        onClick={() => waiveRequirement(item.key, true)}>
                        {isBusy ? '…' : 'Waive'}
                      </Button>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {reviewing && isVerifierRole(reviewing.role) && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>Verifier levels</Typography>
              <Typography variant="caption" color="text.secondary">
                Which work this verifier can do. Drives the sections they see in the verifier app.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                <FormControlLabel
                  control={<Checkbox checked={verifierLevels.includes('client')} onChange={() => toggleLevel('client')} />}
                  label="Client verifications" />
                <FormControlLabel
                  control={<Checkbox checked={verifierLevels.includes('contractor')} onChange={() => toggleLevel('contractor')} />}
                  label="Contractor verifications" />
              </Stack>
              {reviewing.approvalStatus === 'approved' && (
                <Button size="small" variant="outlined" sx={{ mt: 1 }}
                  disabled={verifierBusy || verifierLevels.length === 0}
                  onClick={() => saveVerifierLevels(reviewing.id)}>
                  Save levels
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeReview}>Close</Button>
          {reviewing?.approvalStatus !== 'approved' && reviewing && status === 'pending' && (
            <>
              <Button color="error" variant="outlined" disabled={!!busyId}
                onClick={() => { setRejecting(reviewing); setReason(''); }}>Reject</Button>
              {(() => {
                const checklist = detail?.applicationChecklist;
                const checklistIncomplete = checklist && !checklist.complete;
                const missingLabels = checklistIncomplete
                  ? checklist.missing.map((k) => {
                    const found = checklist.items.find((i) => i.key === k);
                    return found ? found.label : k;
                  })
                  : [];
                const isDisabled = busyId === reviewing.id
                  || (isVerifierRole(reviewing.role) && verifierLevels.length === 0)
                  || checklistIncomplete;
                const btn = (
                  <Button variant="contained" disabled={isDisabled}
                    onClick={() => approve(reviewing.id)}>Approve</Button>
                );
                if (checklistIncomplete) {
                  return (
                    <Tooltip title={`Unmet requirements: ${missingLabels.join(', ')}`} arrow>
                      <span>{btn}</span>
                    </Tooltip>
                  );
                }
                return btn;
              })()}
            </>
          )}
          {reviewing && status !== 'pending' && (
            <Button color="warning" variant="outlined" disabled={busyId === reviewing.id}
              onClick={() => unapprove(reviewing.id)}>
              {status === 'approved' ? 'Un-approve' : 'Move to pending'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejecting} onClose={() => setRejecting(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reject application</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} sx={{ mt: 1 }}
            label="Reason (sent to the applicant)" value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={reason.trim().length < 3 || !!busyId}
            onClick={submitReject}>Reject &amp; notify</Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      {view === 'tierUpgrades' && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Approve or reject merchant verification tier-upgrade requests.
            </Typography>
            <Button variant="outlined" size="small" disabled={tierLoading} onClick={loadTierUpgrades}>
              {tierLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>

          {tierLoading ? <CircularProgress /> : tierUpgrades.length === 0 ? (
            <Typography color="text.secondary">No pending tier upgrades.</Typography>
          ) : (
            <Grid container spacing={2}>
              {tierUpgrades.map((row) => (
                <Grid item xs={12} md={6} key={row.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography sx={{ fontWeight: 600 }}>
                        {row.business_name || 'Unnamed merchant'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Chip size="small" label={formatTier(row.verification_tier)} />
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        <Chip size="small" color="primary" label={formatTier(row.requested_tier)} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Requested: {formatDate(row.updated_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {Number(row.approved_documents ?? 0)} approved docs · {Number(row.completed_orders ?? 0)} completed orders · rating {Number(row.rating_average ?? 0).toFixed(1)} ({Number(row.total_ratings ?? 0)})
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button size="small" variant="contained" disabled={tierBusyId === row.id}
                          onClick={() => resolveTierUpgrade(row, true)}>Approve</Button>
                        <Button size="small" color="error" variant="outlined" disabled={tierBusyId === row.id}
                          onClick={() => resolveTierUpgrade(row, false)}>Reject</Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {view === 'merchantDocs' && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Review merchant KYC documents awaiting verification.
            </Typography>
            <Button variant="outlined" size="small" disabled={docsLoading} onClick={loadMerchantDocs}>
              {docsLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>

          {docsLoading ? <CircularProgress /> : merchantDocs.length === 0 ? (
            <Typography color="text.secondary">No documents awaiting review.</Typography>
          ) : (
            <Grid container spacing={2}>
              {merchantDocs.map((row) => (
                <Grid item xs={12} md={6} key={row.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography sx={{ fontWeight: 600 }}>
                        {row.business_name || 'Unnamed merchant'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Chip size="small" label={(row.document_type || '').replace(/_/g, ' ')} />
                        {row.expiry_date && (
                          <Chip size="small" variant="outlined"
                            color={new Date(row.expiry_date) < new Date() ? 'error' : 'default'}
                            label={`Expires ${formatDate(row.expiry_date)}`} />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Uploaded: {formatDate(row.created_at)}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button size="small" variant="outlined" component="a"
                          href={row.file_url} target="_blank" rel="noopener noreferrer">
                          View file
                        </Button>
                        <Button size="small" variant="contained" disabled={docBusyId === row.id}
                          onClick={() => reviewMerchantDoc(row, 'Approved')}>Approve</Button>
                        <Button size="small" color="error" variant="outlined" disabled={docBusyId === row.id}
                          onClick={() => { setDocRejecting(row); setDocNotes(''); }}>Reject</Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Dialog open={!!docRejecting} onClose={() => setDocRejecting(null)} fullWidth maxWidth="sm">
            <DialogTitle>Reject document</DialogTitle>
            <DialogContent>
              <TextField autoFocus fullWidth multiline minRows={3} sx={{ mt: 1 }}
                label="Reason (sent to the merchant)" value={docNotes}
                onChange={(e) => setDocNotes(e.target.value)} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDocRejecting(null)}>Cancel</Button>
              <Button color="error" variant="contained"
                disabled={docNotes.trim().length < 3 || !!docBusyId}
                onClick={() => reviewMerchantDoc(docRejecting, 'Rejected', docNotes.trim())}>
                Reject &amp; notify
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Dialog open={!!eacOverriding} onClose={() => { setEacOverriding(null); setEacError(''); }} fullWidth maxWidth="sm">
        <DialogTitle>{eacOverriding?.role === 'conveyancer' ? 'Verify conveyancer manually' : 'Verify agency manually'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {eacOverriding?.role === 'conveyancer'
              ? 'Record how you confirmed they may practise as a conveyancer this year (e.g. admission order and practising certificate seen, Law Society called).'
              : 'Record how you confirmed this agency is registered with the Estate Agents Council (e.g. phone call, certificate seen).'}
          </Typography>
          {eacError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setEacError('')}>{eacError}</Alert>}
          <TextField autoFocus fullWidth multiline minRows={3} label="Verification notes" value={eacNotes}
            onChange={(e) => setEacNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEacOverriding(null); setEacError(''); }}>Cancel</Button>
          <Button variant="contained" disabled={eacNotes.trim().length < 3 || eacBusy}
            onClick={() => setEacOverride(eacOverriding, true, eacNotes.trim())}>Mark verified</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast('')} message={toast} />
    </Container>
  );
}
